import React, { useContext, useEffect } from "react"
import { createStackNavigator } from "@react-navigation/stack"
import { useAppSelector } from "../../store"
import OnboardingStack from "./OnboardingStack"
import MainAppTabs from "./MainAppTabs"
import Analytics from "../../utility/analytics"
import * as Sentry from "../../utility/sentry"
import { ThemeContext } from "../../utility/context/ThemeContext"
import { Image, Platform, ScrollView, Text, View } from "react-native"
import { MAX_CONTENT_WIDTH } from "../../config"
import { ShadowView, textStyles } from "../../style"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import PermissionToggle from "../../utility/permission-toggle"
import { ThemedButton } from "../../utility/input/buttons"
import { PlaidProvider, useAuthContext } from "../../utility/context"
import { useWebDocumentTitle } from "../../hooks"

export type AuthenticatedStackParamList = {
  UnapprovedUserStack: undefined
  OnboardingStack: undefined
  App: undefined
}

const Stack = createStackNavigator<AuthenticatedStackParamList>()

const WAITLIST_TITLE = "You're waitlisted!"

const AuthenticatedStack = () => {
  const { user, status } = useAppSelector((state) => state.auth)

  useEffect(() => {
    if (!user || !user._id) {
      Analytics.reset()
      Sentry.setUser(null)
      return
    }
    // define user for sentry reporting
    Sentry.setUser({
      id: user._id.toString(),
      email: user.email,
    })

    // define user for analytics reporting
    Analytics.identifyUser(user._id.toString())
    Analytics.setUserProperties({
      $email: user.email,
      $first_name: user.firstName,
      $last_name: user.lastName,
      ...user.identityInfo,
    })
    if (user.customer?._id) {
      Analytics.setUserGroup('customer', user.customer._id)
    }
  }, [user, user?.identityInfo])

  return (
    <PlaidProvider>
      <Stack.Navigator screenOptions={{ headerShown: false, gestureEnabled: false }} id={undefined}>
        {
          !user?.approved && status !== 'loading' ? (
            <Stack.Screen
              name="UnapprovedUserStack"
              component={InterestGathered}
              options={{ title: WAITLIST_TITLE }}
            />
          ) : (
          !user?.hasOnboarded && status !== 'loading' ? (
            <Stack.Screen
              name="OnboardingStack"
              component={OnboardingStack}
            />
          ) : (
            <Stack.Screen
              name="App"
              component={MainAppTabs}
            />
          )
        )}
      </Stack.Navigator>
    </PlaidProvider>
  )
}

export default AuthenticatedStack

const InterestGathered = () => {
  const { theme } = useContext(ThemeContext)
  const { textMain, bgMain, borderColor, bgSecond, danger } = theme.colors

  const { logout } = useAuthContext()

  const { top, bottom, left, right } = useSafeAreaInsets()

  useWebDocumentTitle(WAITLIST_TITLE)
  
  return (
    <ScrollView
      bounces={false}
      style={{
        flex: 1,
        backgroundColor: bgMain,
        paddingTop: top,
        paddingBottom: bottom,
        paddingLeft: left || 16,
        paddingRight: right || 16,
      }}
      contentContainerStyle={{
        flexGrow: 1,
        flexDirection: "column",
        justifyContent: "space-between", 
        alignSelf: "center",
        maxWidth: MAX_CONTENT_WIDTH,
        width: "100%",
        gap: 30,
        paddingBottom: 10,
      }}
    >
      <View style={{ gap: 20 }}>
        <View
          style={{
            alignSelf: "center",
            width: "50%",
            maxWidth: 200,
            aspectRatio: 1,
          }}
        >
          <Image
            source={theme.name === "dark" ? require("../../assets/images/logo/StackedLogoWhite.png") : require("../../assets/images/logo/StackedLogoBlack.png")}
            style={{width: "100%", height: "100%"}}
            resizeMode="contain"
            resizeMethod="scale"
          />
        </View>
        <Text style={[textStyles.h2, { color: textMain }]}>
          {WAITLIST_TITLE}
        </Text>

        <Text style={[textStyles.body, { color: textMain }]}>
          We&apos;re currently in the process of approving new accounts.
        </Text>

        <Text style={[textStyles.body, { color: textMain }]}>
          Your account is on our waitlist, and we&apos;ll notify you by email{" "}
          as soon as access is available.
        </Text>

        <Text style={[textStyles.body, { color: textMain }]}>
          No action is needed from you right now—thanks for your patience!
        </Text>

        {Platform.OS !== 'web' ? (
          <View style={{ gap: 10 }}>
            <Text style={[textStyles.body, { color: textMain }]}>
              Optionally, you can also receive a push notification when your account is approved{' '}
              by giving Impact Banking permission to send you notifications.
            </Text>
            <ShadowView
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                borderWidth: 1,
                borderColor: borderColor,
                borderRadius: 10,
                padding: 10,
                backgroundColor: bgSecond
              }}
            >
              <Text style={[textStyles.body, { color: textMain }]}>
                Push notifications
              </Text>
              <PermissionToggle permission="notifications" />
            </ShadowView>
          </View>
        ) : null}
      </View>
      
      <View style={{ justifyContent: 'center', alignItems: 'center' }}>
        <ThemedButton
          text="Logout"
          onPress={() => {logout()}}
          variant="secondary"
          style={{
            borderWidth: 0
          }}
          textStyle={{
            color: danger
          }}
        />
      </View>
      
    </ScrollView>
  )
}


